import { SubscriptionProps } from 'services/myAccount/myAccount.types';

import { AdditionalInfo } from './plans.types';

export enum WMDeviceFlow {
  WMDevices = 'wmDevices'
  // WMScales = 'wmScales'
}

export enum ProgramStatus {
  Active = 'active',
  Inactive = 'inactive'
}

export type ProgramCategory =
  'default' | 'mens-health' | 'mental-health' | 'weight-management' | 'women-health';

export const PROGRAM_CATEGORY_LABELS: Record<ProgramCategory, string> = {
  default: 'Default',
  'mens-health': "Men's Health",
  'mental-health': 'Mental Health',
  'weight-management': 'Weight Management',
  'women-health': "Women's HRT"
};

export enum ProgramCode {
  // Legacy memberships
  ConciergeCare = 'concierge-care',
  FlexCare = 'limited-access',
  lifeMDDiscounted = 'lifemd-discounted-access',
  LifeMDMembership = 'membership',
  // LifeMD Plus
  LifeMDPlus = 'lifemd-plus',
  // Mental Health
  MentalHealthAsNeededTreatment = 'mental-health-as-needed-treatment',
  MentalHealthDailyTreatment = 'mental-health-daily-treatment',

  TotalCare = 'total-care',

  UnlimitedMembership = 'unlimited-membership',

  UrgentCare = 'urgent-care',
  WHALaCarte = 'women-health-a-la-carte',
  WhBundleCustomized = 'wh-bundle-customized',

  WhBundleCustomizedEstradiol = 'wh-bundle-customized-estradiol-included',
  // Women's HRT - Bundle/async a.k.a. compound - medication Included
  WhBundleStandard = 'wh-bundle-standard',
  WhBundleStandardEstradiol = 'wh-bundle-standard-estradiol-included',

  // Weight Management - Bundle a.k.a. compound - medication Included
  WMBundleSemaglutide = 'glp-1-weight-management-semaglutide-included',

  WMBundleTirzepatide = 'glp-1-weight-management-tirzepatide-included',
  WMBundleWegovy = 'glp-1-weight-management-wegovy-included',
  // Weight Management - Care Only
  WMCareOnly = 'glp-1-weight-management',
  // Weight Management - Maintenance a.k.a. low dose
  WMMaintenance = 'glp-1-weight-management-maintenance',
  WMMaintenanceSemaglutide = 'glp-1-weight-management-maintenance-semaglutide-included',
  WMMaintenanceTirzepatide = 'glp-1-weight-management-maintenance-tirzepatide-included',
  // Weight Management - Triple Therapy
  WMTripleTherapy = 'triple-therapy-weight-management-medication-included',
  // Women's HRT
  WomenHealthEssentials = 'women-health-essentials',
  WomenHealthHRTClub = 'women-health-hrt-club',
  WomenHealthOptimization = 'women-health-optimization'
}

export enum RequiredAppointmentReason {
  BundledOffer = 'bundled-offer',
  NextRenewal = 'next-renewal',
  /** First appointment deferred from signup (e.g. Men's Health post-signup scheduling). */
  Onboarding = 'onboarding',
  Renewal = 'renewal',
  RenewalInsurance = 'renewal-insurance',
  SyncGap = 'not-taken-medication-for-long-time'
}

interface Plan {
  planId: string;
  pricePointId: string;
}

export enum ComponentItemName {
  dashboardTab = 'dashboardTab'
}

export type ProgramUiConfigByComponent = {
  [ComponentItemName.dashboardTab]: {
    component: ComponentItemName.dashboardTab;
    label: string;
  };
};

export type ProgramUiConfigItem = ProgramUiConfigByComponent[keyof ProgramUiConfigByComponent];

/**
 * One partner coupon on a program, as the backend serializes it (thecvlb/lifemd#17494).
 *
 * A config is 1:1 with a coupon and immutable apart from its status, so the reference cannot drift.
 * Several entries on one program is a *stack* — the AT&T benefit is delivered this way.
 */
export interface PartnerCoupon {
  appliedAt: string;
  code: string;
  configId: string;
  couponId: string;
  partnerId: string;
}

/**
 * What a partner stack is worth on this program, priced by Maxio itself (thecvlb/lifemd#17494).
 *
 * Dollar strings, matching every other price in these payloads. The **recurring** bill, not the first
 * one, so a coupon that only discounts the first invoice is never shown as a permanent price.
 *
 * Absent rather than wrong: the backend withholds it when the recorded stack does not describe the
 * price point the program is actually on. A `discount` of `'0.00'` is a real answer — Maxio applied
 * none of the codes — and is not the same as the field being missing.
 */
export interface PartnerPricing {
  /** What the stack takes off that bill. */
  discount: string;
  /** What the patient is billed once the stack is applied. */
  finalPrice: string;
  /** The recurring bill before the stack — Maxio's own subtotal, which the discount came off. */
  listPrice: string;
}

export interface UserProgram {
  details?: {
    completedInitialAppointment?: boolean;
    miCompletionStatus: boolean;
    preferredMedication?: string | null;
    requiredAppointment: null | {
      appointmentTypeId: string;
      isRequired: boolean;
      reason: RequiredAppointmentReason;
    };
  };
  program: Omit<Program, 'plans'> & {
    /**
     * How the client should cancel this program:
     * - `typeform`: open the program's Typeform downsell/retention flow (default).
     * - `direct`: call the direct-cancel endpoint with a confirmation modal (TEMPORARY, LMD-614 — Women's Health only).
     */
    cancelType?: 'typeform' | 'direct';
    isCancellable?: boolean;
    isIncluded?: boolean;
    /**
     * Partner coupons applied to this program's subscription — the AT&T benefit's carrier. Recorded
     * per program, so a patient can hold partnered and retail-priced programs at the same time.
     */
    partnerCoupons?: PartnerCoupon[];
    /** What those coupons are worth here. Absent when the backend could not price them safely. */
    partnerPricing?: PartnerPricing;
    planPricePointId: string;
    stardustCancellationUrl?: string;
    stardustPostOnboardingUrl?: string;
    // Can be used by fronted to show paywalls/block features if patient downgraded from program or not completed onboarding
    uiConfig: {
      items: ProgramUiConfigItem[];
    };
  };
  subscription?: SubscriptionProps;
}

export interface Program {
  _id: string;
  /** Anonymized indication code from backend (opaque string, e.g. "A"/"B"/...). Optional; omitted when the program has none. */
  analyticsCode?: string;
  benefits: AdditionalInfo[];
  category: ProgramCategory;
  code: ProgramCode;
  doctorId: string;
  name: string;
  plans: Plan[];
  status: ProgramStatus;
}
